import React, { ReactElement, cloneElement } from 'react';
import { Control } from 'react-hook-form';
import { toCamelCase, uniqueId } from '../utils/helpers/common';
import { InputLabel } from './InputLabel';

export interface IInputFieldProps {
  control?: Control<any>;
  disabled?: boolean;
  error?: string;
  isRequired?: boolean;
  label: string;
  name: string;
  placeholder?: string;
  secondaryLabel?: string;
  tooltipText?: string;
}

export interface IInputWrapperProps {
  children?: React.ReactNode;
  endElement?: React.ReactNode;
  errorMessage?: string;
  extraMessageElement?: React.ReactNode;
  isFieldset?: boolean;
  isRequired?: boolean;
  label: string;
  labelClassName?: string;
  startElement?: React.ReactNode;
  tooltipText?: string;
}

export const InputWrapper = ({
  children,
  endElement,
  errorMessage,
  extraMessageElement,
  isFieldset = false,
  isRequired,
  label,
  labelClassName,
  startElement,
  tooltipText,
}: IInputWrapperProps) => {
  const ElementTag = isFieldset ? 'fieldset' : 'div';

  const inputId = 'field-id-' + toCamelCase(label) + '-' + uniqueId();

  const clonedChildren = cloneElement(children as ReactElement, {
    'aria-describedby': `${inputId}-errorMessage`,
    'aria-required': isRequired,
    color: isFieldset || !errorMessage ? undefined : 'error',
    id: inputId,
  });

  return (
    <ElementTag className="form-control w-full">
      <InputLabel
        label={label}
        className={labelClassName}
        inputId={isFieldset ? '' : inputId}
        isLegend={isFieldset}
        isRequired={isRequired}
        tooltipText={tooltipText}
      />
      {isFieldset ? (
        clonedChildren
      ) : (
        <div className="input-group">
          {startElement}
          {clonedChildren}
          {endElement}
        </div>
      )}
      {(errorMessage || extraMessageElement) && (
        <div
          id={`${inputId}-errorMessage`}
          aria-live="polite"
          className="label"
        >
          {errorMessage ? (
            <span className="label-text-alt text-error">{errorMessage}</span>
          ) : (
            <span></span>
          )}
          {extraMessageElement && (
            <span className="label-text-alt">{extraMessageElement}</span>
          )}
        </div>
      )}
    </ElementTag>
  );
};
